import gql from 'graphql-tag';
import { useQuery } from 'urql';

import { GameDescriptor } from '@/fragments/GameDescriptor';
import { useQueryParamsWithTime } from '@/hooks';
import { mapVariables } from '@/utils';
import { makeOrderBy, makePagination } from '@/utils/pagination';
import {
  TransactionsQuery,
  TransactionsQueryVariables,
} from './__generated__/useTransactions';
import { queryParams } from './queryParams';
import { applyDefaultThreeMonthFilter } from './utils';

const TRANSACTIONS_QUERY = gql`
  query Transactions(
    $adjustmentCategory: AdjustmentCategory
    $adjustmentType: AdjustmentType
    $after: String
    $before: String
    $brand: BrandEnum
    $currency: String
    $desc: Boolean
    $first: Int
    $from: OffsetDateTime
    $id: String
    $last: Int
    $maxAmount: PositiveBigDecimal
    $minAmount: PositiveBigDecimal
    $orderBy: TransactionSearchOrder
    $paymentMethod: String
    $paymentStatus: [PaymentStatus!]
    $playerId: ID
    $rawId: String
    $to: OffsetDateTime
    $transactionTypes: [TransactionType!]
  ) {
    viewer {
      id
      brands {
        code
        name
      }
      transactionsV2(
        adjustmentCategory: $adjustmentCategory
        adjustmentType: $adjustmentType
        after: $after
        before: $before
        brand: $brand
        currency: $currency
        desc: $desc
        first: $first
        from: $from
        id: $id
        last: $last
        maxAmount: $maxAmount
        minAmount: $minAmount
        orderBy: $orderBy
        paymentMethod: $paymentMethod
        paymentStatus: $paymentStatus
        playerId: $playerId
        rawId: $rawId
        to: $to
        transactionTypes: $transactionTypes
      ) {
        edges {
          node {
            rawTransactionId
            playerId
            transactionId
            gameId
            gameRoundId
            isRoundFinished
            extTransactionId
            payload
            gameSessionId
            gameProvider
            gameDescriptor: gameDescriptorV2 {
              ...GameDescriptor
            }
            brand {
              name
            }
            processedAt
            paymentMethod
            paymentStatus
            transactionType
            openingBalance
            closingBalance
            currency
            comment
            amount
            paymentFee {
              money {
                amount
                currency
              }
            }
            initiator {
              ... on PlayerInitiator {
                player {
                  firstName
                  lastName
                  email
                  id
                }
              }
              ... on AgentInitiator {
                agent {
                  email
                  firstName
                  lastName
                }
              }
            }
            bonusTransactionData {
              bonusId
              amount
              openingBalance
              closingBalance
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
      }
    }
  }
  ${GameDescriptor}
`;

export default function useTransactions() {
  const [query, setQuery] = useQueryParamsWithTime(queryParams, {
    fromAndToOptional: true,
  });

  const finalQuery = applyDefaultThreeMonthFilter(query);

  const [{ data, fetching }, refresh] = useQuery<
    TransactionsQuery,
    TransactionsQueryVariables
  >({
    query: TRANSACTIONS_QUERY,
    variables: mapVariables(finalQuery),
  });

  return {
    refresh,
    fetching,
    defaultFilter: finalQuery,
    updateFilter: (values: {}) => {
      setQuery(values, 'replaceIn');
    },
    ...makeOrderBy(finalQuery, setQuery),
    ...makePagination(
      finalQuery,
      setQuery,
      data?.viewer.transactionsV2.pageInfo,
    ),
    brands: data?.viewer.brands,
    transactions: data?.viewer.transactionsV2?.edges?.map((edge) => edge?.node),
  };
}
